import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Download, X, ShareNetwork, Plus } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Floating "Install as App" prompt.
 * - On Chrome/Edge/Android: uses the native `beforeinstallprompt` event.
 * - On iOS Safari: shows manual instructions ("Compartir → Agregar a inicio").
 * - Hides itself if already installed or dismissed in localStorage.
 *
 * SCOPE: Only shown to AUTHENTICATED Commandix users (real account holders)
 * when navigating inside the CRM / owner-panel. Public-facing pages — Hanoy's
 * booking link, course catalog, public landing, login/signup forms — never
 * show this prompt. Those visitors are tenant CLIENTS (Hanoy's customers),
 * not Commandix users, and pushing them to install the SaaS would be
 * intrusive and off-brand. Commandix's own promo lives in the page footer.
 */
export default function InstallPwaPrompt() {
  const { user } = useAuth();
  const location = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("pwa-install-dismissed") === "1"
  );

  // Detect iOS (Safari only — Chrome iOS uses Safari engine but lacks the share menu)
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  // Routes where the install prompt is NEVER shown (public / tenant-client
  // facing or auth-entry pages). Everything else is internal CRM / owner panel.
  const path = location.pathname || "/";
  const isPublicRoute =
    path === "/" ||
    path.startsWith("/b/") ||
    path.startsWith("/login") ||
    path.startsWith("/signup") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/verify-device") ||
    path.startsWith("/portal-public/") ||
    path.startsWith("/cita/");

  useEffect(() => {
    if (isStandalone || dismissed) return;
    if (!user || isPublicRoute) return;

    // Chromium / Edge / Android Chrome
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS hint: show after 4s if iOS and not installed
    let iosTimer;
    if (isIos) {
      iosTimer = setTimeout(() => setShowIosHint(true), 4000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, [isStandalone, dismissed, isIos, user, isPublicRoute]);

  const dismiss = () => {
    localStorage.setItem("pwa-install-dismissed", "1");
    setDismissed(true);
    setDeferred(null);
    setShowIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") dismiss();
    setDeferred(null);
  };

  if (isStandalone || dismissed) return null;
  // Hard gate: never show on public / auth-entry routes, and never to
  // unauthenticated visitors (tenant clients have no Commandix account).
  if (!user || isPublicRoute) return null;

  // ===== Android / Chromium: native button
  if (deferred) {
    return (
      <div
        className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-sm z-50 rounded-xl border bg-card shadow-2xl p-3"
        data-testid="pwa-install-prompt"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-amber-500/15 text-amber-400 p-2">
            <Download size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold">Instalar Commandix</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Agrégalo a tu pantalla de inicio para acceder rápido y recibir notificaciones.
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                onClick={install}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="pwa-install-btn"
              >
                Instalar
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-install-dismiss">
                Ahora no
              </Button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ===== iOS: manual instructions
  if (isIos && showIosHint) {
    return (
      <div
        className="fixed bottom-20 left-3 right-3 z-50 rounded-xl border bg-card shadow-2xl p-3"
        data-testid="pwa-ios-hint"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-amber-500/15 text-amber-400 p-2">
            <Download size={20} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold">Instalar Commandix en tu iPhone</h3>
            <ol className="text-xs text-muted-foreground mt-1 space-y-1 list-decimal list-inside">
              <li className="flex items-center gap-1">
                Toca <ShareNetwork size={14} weight="bold" className="inline text-sky-400" /> en la barra inferior de Safari
              </li>
              <li className="flex items-center gap-1">
                Selecciona "Agregar a inicio" <Plus size={14} weight="bold" className="inline" />
              </li>
              <li>Confirma "Agregar"</li>
            </ol>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="mt-2 text-xs"
              data-testid="pwa-ios-dismiss"
            >
              Entendido
            </Button>
          </div>
          <button
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
